import React from "react";
import PropTypes from "prop-types";
import useAutocomplete from "@mui/material/useAutocomplete";
// or: import useAutocomplete from '@mui/base/useAutocomplete';
import { Popper } from "@mui/material";
import { FixedSizeList } from "react-window";

export default function SimpleVirtualizedAutocompletePopper({
  label,
  options,
  value,
  onChange,
  optionKey = "id",
  optionLabel = "label",
  placeholder,
  disabled = false,
  itemSize = 40,
  maxVisibleItems = 8,
  className = "",
  noOptionsText = "No options found",
  popperPlacement = "bottom-start",
  ...props
}) {
  const {
    getRootProps,
    getInputProps,
    getInputLabelProps,
    getListboxProps,
    getOptionProps,
    groupedOptions,
    setAnchorEl,
    anchorEl,
    focused,
    popupOpen,
  } = useAutocomplete({
    options,
    value,
    getOptionLabel: (option) => option?.[optionLabel] ?? "",
    isOptionEqualToValue: (option, val) =>
      String(option?.[optionKey]) === String(val?.[optionKey]),
    onChange: (event, newValue) => onChange?.(event, newValue),
    disabled,
    ...props,
  });

  // Renders each row in the virtualized list
  const Row = ({ index, style }) => {
    const option = groupedOptions[index];
    const optionProps = getOptionProps({ option, index });
    return (
      <li
        {...optionProps}
        style={style}
        className="px-3 py-2 text-sm text-gray-700 cursor-pointer hover:bg-gray-100"
      >
        {option?.[optionLabel]}
      </li>
    );
  };

  return (
    <div
      {...getRootProps()}
      className={`flex flex-col ${className} focus-within:border-sss-info focus-within:ring-1 focus-within:ring-sss-info`}
    >
      {/* Optional label */}
      {label && (
        <label
          {...getInputLabelProps()}
          className="mb-1 text-sm font-medium text-gray-700"
        >
          {label}
        </label>
      )}

      {/* The anchor element for the Popper */}
      <div
        ref={setAnchorEl}
        className={`
          relative w-full
          ${disabled ? "bg-gray-100 pointer-events-none" : ""}
        `}
      >
        <input
          {...getInputProps()}
          placeholder={placeholder}
          disabled={disabled}
          className="w-full h-full outline-none bg-transparent text-sm text-gray-800"
        />
      </div>

      {/* Popper for the dropdown list */}
      <Popper
        open={popupOpen && groupedOptions.length > 0}
        anchorEl={anchorEl}
        placement={popperPlacement}
        style={{ zIndex: 9999 }}
      >
        {/* The container for virtualized list */}
        <div
          {...getListboxProps()}
          className="w-full bg-white border border-gray-200 rounded-md shadow-lg overflow-hidden"
          style={{
            width: anchorEl?.clientWidth || 300,
            maxHeight: maxVisibleItems * itemSize,
          }}
        >
          <FixedSizeList
            height={Math.min(maxVisibleItems, groupedOptions.length) * itemSize}
            itemCount={groupedOptions.length}
            itemSize={itemSize}
            width="100%"
            style={{ overflowX: "hidden" }}
          >
            {Row}
          </FixedSizeList>
        </div>
      </Popper>

      {/* “No options” fallback */}
      {popupOpen && groupedOptions.length === 0 && focused && !disabled && (
        <Popper
          open
          anchorEl={anchorEl}
          placement={popperPlacement}
          style={{ zIndex: 9999 }}
        >
          <div
            className="w-full px-3 py-2 bg-white border border-gray-200
                       rounded-md shadow-lg text-sm text-gray-500"
            style={{ width: anchorEl?.clientWidth || 300 }}
          >
            {noOptionsText}
          </div>
        </Popper>
      )}
    </div>
  );
}

SimpleVirtualizedAutocompletePopper.propTypes = {
  label: PropTypes.string,
  options: PropTypes.array.isRequired,
  value: PropTypes.any,
  onChange: PropTypes.func,
  optionKey: PropTypes.string,
  optionLabel: PropTypes.string,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  itemSize: PropTypes.number,
  maxVisibleItems: PropTypes.number,
  className: PropTypes.string,
  noOptionsText: PropTypes.string,
  popperPlacement: PropTypes.string,
};
